import { useEffect, useRef, type ReactNode } from 'react';
import { create } from 'zustand';

export interface Command {
  id: string;
  title: string;
  group: string;
  icon?: ReactNode;
  /** Подсказка справа: «⌘K», «N». */
  shortcut?: string;
  /** Подзаголовок (например, воркспейс доски). */
  hint?: string;
  /** Дополнительные слова для поиска. */
  keywords?: string;
  disabled?: boolean;
  run: () => void | Promise<void>;
}

interface CommandsState {
  sources: Record<string, Command[]>;
  open: boolean;
  setOpen: (open: boolean) => void;
  setSource: (key: string, cmds: Command[] | null) => void;
}

/** Реестр команд палитры: страницы регистрируют свои команды, пока смонтированы. */
export const useCommands = create<CommandsState>((set) => ({
  sources: {},
  open: false,
  setOpen: (open) => set({ open }),
  setSource: (key, cmds) =>
    set((s) => {
      const next = { ...s.sources };
      if (cmds) next[key] = cmds;
      else delete next[key];
      return { sources: next };
    }),
}));

export function openCommandPalette(): void {
  useCommands.getState().setOpen(true);
}

/**
 * Регистрирует команды на время жизни компонента.
 * `build` вызывается при каждом открытии палитры — команды всегда актуальны.
 */
export function useRegisterCommands(key: string, build: () => Command[]): void {
  const ref = useRef(build);
  ref.current = build;
  const open = useCommands((s) => s.open);
  useEffect(() => {
    if (!open) return;
    useCommands.getState().setSource(key, ref.current());
  }, [open, key]);
  useEffect(() => () => useCommands.getState().setSource(key, null), [key]);
}

// ─── Поиск ───────────────────────────────────────────────────────────

const EN = "qwertyuiop[]asdfghjkl;'zxcvbnm,.`";
const RU = 'йцукенгшщзхъфывапролджэячсмитьбюё';
const toRu = new Map([...EN].map((c, i) => [c, RU[i]!]));
const toEn = new Map([...RU].map((c, i) => [c, EN[i]!]));

/** Запрос, набранный не в той раскладке: «cnbrth» → «стикер», «ыуфкср» → «search». */
export function swapLayout(q: string): string {
  let out = '';
  for (const ch of q.toLowerCase()) out += toRu.get(ch) ?? toEn.get(ch) ?? ch;
  return out;
}

/** Оценка совпадения: 0 — нет; выше — лучше. Подстрока, начало слова, подпоследовательность. */
export function scoreMatch(text: string, query: string): number {
  const t = text.toLowerCase().replace(/ё/g, 'е');
  const q = query.toLowerCase().replace(/ё/g, 'е').trim();
  if (!q) return 1;
  const idx = t.indexOf(q);
  if (idx === 0) return 100;
  if (idx > 0) return /[\s\-—/«(]/.test(t[idx - 1]!) ? 80 : 60;
  // Все слова запроса встречаются
  const words = q.split(/\s+/);
  if (words.length > 1 && words.every((w) => t.includes(w))) return 50;
  // Подпоследовательность (по первым буквам и т.п.)
  let i = 0;
  for (const ch of t) if (ch === q[i]) i++;
  if (i === q.length) return 20 - Math.min(15, t.length - q.length) / 2;
  return 0;
}

export function matchCommand(c: Command, query: string): number {
  if (!query.trim()) return 1;
  const hay = `${c.title} ${c.keywords ?? ''} ${c.hint ?? ''}`;
  return Math.max(scoreMatch(hay, query), scoreMatch(hay, swapLayout(query)) * 0.9);
}
